<template>
  <HrPageLayout
    title="Payroll History"
    subtitle="Paid payroll runs synced to employee records and payslips"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Finance' },
      { label: 'Payroll History' }
    ]"
  >
    <div v-if="loading && !data" class="hr-loading-wrap">
      <div class="hr-spinner"></div>
      <p>Loading payroll history...</p>
    </div>

    <template v-else>
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-receipt"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Paid runs</p>
          <h2 class="hr-metric-value">{{ data?.summary?.paid_runs ?? 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-wallet"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total net paid</p>
          <h2 class="hr-metric-value">{{ formatCurrency(data?.summary?.total_net) }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Payroll lines</p>
          <h2 class="hr-metric-value">{{ data?.summary?.total_lines ?? 0 }}</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid hr-gap-3 hr-align-end">
            <div class="hr-filter-group">
              <label class="hr-form-label">Payroll group</label>
              <select v-model.number="filters.configId" class="hr-select" @change="reload">
                <option :value="0">All groups</option>
                <option v-for="c in data?.configs || []" :key="c.id" :value="Number(c.id)">{{ c.config_name }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Year</label>
              <select v-model.number="filters.year" class="hr-select" @change="reload">
                <option :value="0">All years</option>
                <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Month</label>
              <select v-model.number="filters.month" class="hr-select" @change="reload">
                <option :value="0">All months</option>
                <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
              </select>
            </div>
            <div class="hr-filter-group hr-flex hr-gap-2">
              <button type="button" class="hr-btn hr-btn-secondary" :disabled="backfilling" @click="resetFilters">
                <i class="bx bx-reset"></i> Reset
              </button>
              <button type="button" class="hr-btn hr-btn-primary" :disabled="backfilling" @click="backfillAll">
                <i class="bx bx-sync" :class="{ 'bx-spin': backfilling }"></i>
                Sync all paid runs
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-history hr-me-2"></i>Paid payroll runs</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-text-center hr-py-4">
            <div class="hr-spinner hr-spinner-sm"></div>
          </div>
          <div v-else-if="!runs.length" class="hr-empty-state">
            <i class="bx bx-folder-open"></i>
            <p>No paid payroll runs yet. Approve and mark a run as paid from Payroll Hub.</p>
            <router-link to="/payroll-hub" class="hr-btn hr-btn-primary hr-mt-2">Open Payroll Hub</router-link>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
              <tr>
                <th>Period</th>
                <th>Group</th>
                <th class="hr-text-center">Employees</th>
                <th class="hr-text-end">Gross</th>
                <th class="hr-text-end">Net</th>
                <th>Marked paid</th>
                <th class="hr-text-center">History rows</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <template v-for="run in runs" :key="run.id">
                <tr>
                  <td><strong>{{ run.period_label }}</strong></td>
                  <td>{{ run.config_name }}</td>
                  <td class="hr-text-center">{{ run.employee_count }}</td>
                  <td class="hr-text-end">{{ formatCurrency(run.total_gross) }}</td>
                  <td class="hr-text-end">{{ formatCurrency(run.total_net) }}</td>
                  <td>{{ run.paid_at_formatted || '—' }}</td>
                  <td class="hr-text-center">
                    <span class="hr-badge" :class="historyBadgeClass(run)">{{ run.history_records }}</span>
                  </td>
                  <td class="hr-text-end">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline" @click="toggleRun(run.id)">
                      {{ expandedRunId === run.id ? 'Hide' : 'Details' }}
                    </button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary hr-ms-1" :disabled="backfilling" @click="syncRun(run.id)">
                      Sync
                    </button>
                  </td>
                </tr>
                <tr v-if="expandedRunId === run.id">
                  <td colspan="8" class="hr-p-0">
                    <div v-if="detailLoading" class="hr-p-4 hr-text-center"><div class="hr-spinner hr-spinner-sm"></div></div>
                    <div v-else-if="runDetail" class="run-detail-panel">
                      <table class="hr-table hr-table-sm hr-mb-0">
                        <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Emp No</th>
                          <th class="hr-text-end">Gross</th>
                          <th class="hr-text-end">Deductions</th>
                          <th class="hr-text-end">Net</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="tx in runDetail.transactions" :key="tx.id">
                          <td>{{ tx.employee_name }}</td>
                          <td>{{ tx.empNo }}</td>
                          <td class="hr-text-end">{{ formatCurrency(tx.gross_salary) }}</td>
                          <td class="hr-text-end">{{ formatCurrency(tx.total_deductions) }}</td>
                          <td class="hr-text-end">{{ formatCurrency(tx.net_salary) }}</td>
                          <td><span class="hr-badge hr-badge-success">{{ tx.payment_status }}</span></td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "PayrollHistory",
  components: { HrPageLayout },

  data() {
    const y = new Date().getFullYear();
    return {
      loading: false,
      backfilling: false,
      data: null,
      filters: { year: 0, month: 0, configId: 0, page: 1 },
      expandedRunId: null,
      runDetail: null,
      detailLoading: false,
      yearOptions: [y, y - 1, y - 2, y - 3],
    };
  },

  computed: {
    runs() {
      return this.data?.runs || [];
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    async post(fn, extra = {}) {
      const fd = new FormData();
      fd.append("function", fn);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      return execute(fd, authUrl);
    },

    async load() {
      this.loading = true;
      try {
        const res = await this.post("getCompanyPayrollHistory", {
          page: this.filters.page,
          limit: 24,
          ...(this.filters.year ? { year: this.filters.year } : {}),
          ...(this.filters.month ? { month: this.filters.month } : {}),
          ...(this.filters.configId ? { payroll_config_id: this.filters.configId } : {}),
        });
        if (res?.data?.success) {
          this.data = res.data.data;
        } else {
          throw new Error(res?.data?.message || "Failed to load");
        }
      } catch (e) {
        this.$toast?.error(e.message || "Failed to load payroll history");
      } finally {
        this.loading = false;
      }
    },

    reload() {
      this.filters.page = 1;
      this.load();
    },

    resetFilters() {
      this.filters = { year: 0, month: 0, configId: 0, page: 1 };
      this.load();
    },

    historyBadgeClass(run) {
      const lines = Number(run.employee_count) || 0;
      const rows = Number(run.history_records) || 0;
      return rows >= lines && lines > 0 ? "hr-badge-success" : "hr-badge-warning";
    },

    async loadRunDetail(runId) {
      this.detailLoading = true;
      try {
        const res = await this.post("getPayrollHistoryRunDetail", { payroll_run_id: runId });
        if (res?.data?.success) {
          this.runDetail = res.data.data;
        }
      } catch (e) {
        this.$toast?.error("Could not load run detail");
      } finally {
        this.detailLoading = false;
      }
    },

    async toggleRun(runId) {
      if (this.expandedRunId === runId) {
        this.expandedRunId = null;
        this.runDetail = null;
        return;
      }
      this.expandedRunId = runId;
      this.runDetail = null;
      await this.loadRunDetail(runId);
    },

    async syncRun(runId) {
      this.backfilling = true;
      try {
        const res = await this.post("backfillPayrollHistory", { payroll_run_id: runId });
        if (res?.data?.success) {
          const s = res.data.data || {};
          this.$toast?.success(`Synced ${(s.synced || 0) + (s.updated || 0)} records`);
          await this.load();
          if (this.expandedRunId === runId) await this.loadRunDetail(runId);
        } else {
          throw new Error(res?.data?.message);
        }
      } catch (e) {
        this.$toast?.error(e.message || "Sync failed");
      } finally {
        this.backfilling = false;
      }
    },

    async backfillAll() {
      if (!confirm("Sync payroll history for all paid runs? This updates employee payroll records.")) return;
      this.backfilling = true;
      try {
        const res = await this.post("backfillPayrollHistory");
        if (res?.data?.success) {
          const s = res.data.data || {};
          this.$toast?.success(`Backfill complete: ${s.synced || 0} created, ${s.updated || 0} updated across ${s.runs || 0} runs`);
          await this.load();
        } else {
          throw new Error(res?.data?.message);
        }
      } catch (e) {
        this.$toast?.error(e.message || "Backfill failed");
      } finally {
        this.backfilling = false;
      }
    },

    formatCurrency(val) {
      const n = Number(val) || 0;
      return "KES " + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    monthName(m) {
      return new Date(2000, m - 1, 1).toLocaleString(undefined, { month: "long" });
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.run-detail-panel {
  background: var(--hr-surface-muted, #f8fafc);
  padding: 1rem;
  border-top: 1px solid var(--hr-border, #e2e8f0);
}

.hr-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--hr-text-muted, #64748b);
}

.hr-empty-state i {
  font-size: 2.5rem;
  display: block;
  margin-bottom: 0.75rem;
}
</style>
